import React, { useState } from 'react';
import { Table, Tag, Space, Input, DatePicker, Select, Button, Card, Typography, Slider, Row, Col, Statistic, Avatar } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined, ClockCircleOutlined, DatabaseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

interface ActivityLog {
    key: string;
    timestamp: string;
    query: string;
    user: string;
    duration: number; // in ms
    database: string;
    source: string;
    status: 'success' | 'error' | 'running';
}

const mockActivityData: ActivityLog[] = [
    {
        key: '1',
        timestamp: '2025-05-27 10:30:45',
        query: 'SELECT * FROM orders WHERE status = "pending" AND created_at > "2024-01-01" LIMIT 100',
        user: 'aman.ruhela',
        duration: 120,
        database: 'Snowflake',
        source: 'Native Query',
        status: 'success',
    },
    {
        key: '2',
        timestamp: '2025-05-27 10:29:12',
        query: 'UPDATE inventory SET quantity = quantity - 1 WHERE product_id = 99281',
        user: 'system_bot',
        duration: 45,
        database: 'PostgreSQL',
        source: 'Scheduler: Inventory Sync',
        status: 'success',
    },
    {
        key: '3',
        timestamp: '2025-05-27 10:28:00',
        query: 'SELECT count(*) FROM user_events WHERE event_type = "login" GROUP BY date',
        user: 'sarah.connor',
        duration: 1500,
        database: 'BigQuery',
        source: 'Dashboard: User Activity',
        status: 'running',
    },
    {
        key: '4',
        timestamp: '2025-05-27 10:25:30',
        query: 'DELETE FROM temp_logs WHERE created_at < NOW() - INTERVAL 7 DAY',
        user: 'admin',
        duration: 320,
        database: 'MySQL',
        source: 'Automation Action',
        status: 'success',
    },
    {
        key: '5',
        timestamp: '2025-05-27 10:15:22',
        query: 'SELECT * FROM heavily_nested_view LIMIT 10',
        user: 'junior.dev',
        duration: 5400,
        database: 'Redshift',
        source: 'Native Query',
        status: 'error',
    },
    {
        key: '6',
        timestamp: '2025-05-27 10:10:10',
        query: 'SELECT id, name, email FROM users WHERE last_login IS NULL',
        user: 'marketing_tool',
        duration: 210,
        database: 'PostgreSQL',
        source: 'App: Marketing Exporter',
        status: 'success',
    },
];

const SystemActivityPage: React.FC = () => {
    const [searchText, setSearchText] = useState('');
    const [selectedDuration, setSelectedDuration] = useState<[number, number]>([0, 10000]);

    // Columns definition
    const columns: ColumnsType<ActivityLog> = [
        {
            title: 'QID',
            dataIndex: 'key',
            key: 'key',
            width: 70,
            render: (text) => <Text type="secondary" style={{ fontSize: 13 }}>#{text}</Text>,
        },
        {
            title: 'Timestamp',
            dataIndex: 'timestamp',
            key: 'timestamp',
            width: 180,
            render: (text) => (
                <Space style={{ whiteSpace: 'nowrap' }}>
                    <ClockCircleOutlined style={{ color: '#8c8c8c' }} />
                    <Text style={{ color: '#595959' }}>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Query',
            dataIndex: 'query',
            key: 'query',
            render: (text) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                        fontFamily: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace',
                        fontSize: '13px',
                        color: '#2f54eb',
                        background: '#f0f5ff',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #d6e4ff',
                        maxWidth: '380px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer'
                    }}>
                        {text}
                    </div>
                </div>
            ),
        },
        {
            title: 'User',
            dataIndex: 'user',
            key: 'user',
            width: 150,
            render: (text) => (
                <Space>
                    <Avatar
                        size={24}
                        style={{
                            backgroundColor: text === 'system_bot' ? '#ffec3d' : '#87d068',
                            color: text === 'system_bot' ? '#d48806' : '#fff',
                            fontSize: 12
                        }}
                    >
                        {text.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Text style={{ fontWeight: 500 }}>{text}</Text>
                </Space>
            ),
        },
        {
            title: 'Duration',
            dataIndex: 'duration',
            key: 'duration',
            width: 120,
            sorter: (a, b) => a.duration - b.duration,
            render: (duration) => {
                let color = '#52c41a'; // Green
                let bg = '#f6ffed';
                let border = '#b7eb8f';

                if (duration > 1000) {
                    color = '#fa8c16'; // Orange
                    bg = '#fff7e6';
                    border = '#ffd591';
                }
                if (duration > 5000) {
                    color = '#f5222d'; // Red
                    bg = '#fff1f0';
                    border = '#ffccc7';
                }

                return (
                    <div style={{
                        color: color,
                        background: bg,
                        border: `1px solid ${border}`,
                        padding: '2px 8px',
                        borderRadius: '12px',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'inline-block',
                        minWidth: '60px'
                    }}>
                        {duration} ms
                    </div>
                );
            },
        },
        {
            title: 'Database',
            dataIndex: 'database',
            key: 'database',
            width: 140,
            filters: [
                { text: 'Snowflake', value: 'Snowflake' },
                { text: 'PostgreSQL', value: 'PostgreSQL' },
                { text: 'BigQuery', value: 'BigQuery' },
                { text: 'MySQL', value: 'MySQL' },
            ],
            onFilter: (value, record) => record.database.indexOf(value as string) === 0,
            render: (text) => {
                let icon = <DatabaseOutlined />;
                if (text === 'BigQuery') icon = <DatabaseOutlined style={{ color: '#13c2c2' }} />;
                if (text === 'Snowflake') icon = <DatabaseOutlined style={{ color: '#1890ff' }} />;
                return (
                    <Space>
                        {icon}
                        <Text>{text}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Source',
            dataIndex: 'source',
            key: 'source',
            width: 160,
            filters: [
                { text: 'Dashboard', value: 'Dashboard' },
                { text: 'Scheduler', value: 'Scheduler' },
                { text: 'Native Query', value: 'Native Query' },
            ],
            onFilter: (value, record) => record.source.indexOf(value as string) >= 0,
            render: (text) => (
                <Tag color="geekblue" style={{ borderRadius: 10 }}>{text}</Tag>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const color = status === 'success' ? 'success' : status === 'error' ? 'error' : 'processing';
                return <Tag color={color} style={{ textTransform: 'uppercase', fontWeight: 600 }}>{status}</Tag>;
            }
        }
    ];

    return (
        <div className="system-activity-page" style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100%' }}>

            {/* Header */}
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={3} style={{ marginBottom: 4, color: '#262626' }}>System Activity</Title>
                    <Text type="secondary">Real-time monitoring of queries and system performance</Text>
                </div>
                <Space>
                    <Button icon={<ReloadOutlined />}>Refresh</Button>
                    <Button type="primary" icon={<FilterOutlined />}>Saved Filters</Button>
                </Space>
            </div>

            {/* Summary Cards - Moved to Top */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card bordered={false} hoverable style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title={<Text type="secondary">Total Queries (Today)</Text>}
                            value={12450}
                            valueStyle={{ color: '#1890ff', fontWeight: 600 }}
                            prefix={<DatabaseOutlined style={{ background: '#e6f7ff', padding: 8, borderRadius: '50%', color: '#1890ff', marginRight: 8 }} />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false} hoverable style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title={<Text type="secondary">Avg Query Duration</Text>}
                            value={245}
                            suffix="ms"
                            valueStyle={{ color: '#52c41a', fontWeight: 600 }}
                            prefix={<ClockCircleOutlined style={{ background: '#f6ffed', padding: 8, borderRadius: '50%', color: '#52c41a', marginRight: 8 }} />}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card bordered={false} hoverable style={{ borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                        <Statistic
                            title={<Text type="secondary">Error Rate</Text>}
                            value={1.2}
                            suffix="%"
                            valueStyle={{ color: '#f5222d', fontWeight: 600 }}
                            prefix={<InfoCircleOutlined style={{ background: '#fff1f0', padding: 8, borderRadius: '50%', color: '#f5222d', marginRight: 8 }} />}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters Section */}
            <Card bordered={false} style={{ marginBottom: 24, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }} bodyStyle={{ padding: '20px 24px' }}>
                <Row gutter={[24, 16]} align="middle">
                    <Col xs={24} md={8} lg={6}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Search</Text>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            placeholder="Query ID, content or error..."
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            allowClear
                            style={{ borderRadius: 6 }}
                        />
                    </Col>
                    <Col xs={24} md={8} lg={4}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>User</Text>
                        <Select placeholder="All Users" style={{ width: '100%' }} allowClear bordered={true}>
                            <Option value="aman.ruhela">aman.ruhela</Option>
                            <Option value="system_bot">system_bot</Option>
                            <Option value="admin">admin</Option>
                        </Select>
                    </Col>
                    <Col xs={24} md={8} lg={6}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Time Range</Text>
                        <RangePicker style={{ width: '100%', borderRadius: 6 }} showTime />
                    </Col>
                    <Col xs={24} md={12} lg={8}>
                        <div style={{ padding: '0 8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <Text strong>Duration</Text>
                                <Text type="secondary">{selectedDuration[0]}ms - {selectedDuration[1]}ms</Text>
                            </div>
                            <Slider
                                range
                                value={selectedDuration}
                                max={10000}
                                onChange={(value) => setSelectedDuration(value as [number, number])}
                                trackStyle={[{ backgroundColor: '#1890ff' }]}
                                handleStyle={[{ borderColor: '#1890ff' }, { borderColor: '#1890ff' }]}
                            />
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Data Table */}
            <Card bordered={false} style={{ borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }} bodyStyle={{ padding: 0 }}>
                <Table
                    columns={columns}
                    dataSource={mockActivityData}
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    rowClassName="activity-row"
                    onRow={() => ({
                        style: { cursor: 'pointer' }
                    })}
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ margin: 0, padding: '20px 32px', background: '#f8f9fa', borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0' }}>
                                <Row>
                                    <Col span={24}>
                                        <Text strong style={{ fontSize: 14 }}>SQL Query</Text>
                                        <div style={{
                                            marginTop: 12,
                                            padding: 16,
                                            background: '#282c34',
                                            color: '#abb2bf',
                                            fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                                            borderRadius: 6,
                                            fontSize: 13,
                                            lineHeight: 1.6,
                                            border: '1px solid #e8e8e8',
                                            boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.1)'
                                        }}>
                                            {record.query}
                                        </div>
                                    </Col>
                                </Row>
                                <Row style={{ marginTop: 16 }} gutter={32}>
                                    <Col>
                                        <Statistic title="Execution Time" value={record.duration} suffix="ms" valueStyle={{ fontSize: 16 }} />
                                    </Col>
                                    <Col>
                                        <Statistic title="Database" value={record.database} valueStyle={{ fontSize: 16 }} />
                                    </Col>
                                    <Col>
                                        <Statistic title="Initiated By" value={record.user} valueStyle={{ fontSize: 16 }} />
                                    </Col>
                                </Row>
                            </div>
                        ),
                        expandRowByClick: true
                    }}
                />
            </Card>
        </div>
    );
};

export default SystemActivityPage;
